import { useState } from 'react';
import { Shield, Clock, Key, CheckCircle, Globe, Zap, ArrowRight, Copy, Check } from 'lucide-react';

export default function HomeScreen() {
    const [demoKey, setDemoKey] = useState('');
    const [copied, setCopied] = useState(false);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDemoSubmit = () => {
        if (demoKey.trim()) {
            window.open(`${window.location.origin}/${demoKey}`, '_blank');
        }
    };

    const url = `${window.location.host}/${ demoKey || "your-unique-key"}`;
    const features = [
        {
            icon: Shield,
            title: "Bank-Grade Security",
            description: "Your files are protected with enterprise-level encryption and secure authentication protocols."
        },
        {
            icon: Clock,
            title: "Time-Limited Access",
            description: "Download links automatically expire after 5 minutes to prevent unauthorized access."
        },
        {
            icon: Key,
            title: "Unique Access Keys",
            description: "Each file has a unique access key that must be combined with the correct password."
        },
        {
            icon: Zap,
            title: "Instant Access",
            description: "Files open immediately in a new tab once authenticated, with manual download backup."
        }
    ];

    const steps = [
        {
            step: "1",
            title: "Get Your Access Link",
            description: "Receive a unique URL with your file's access key from the sender"
        },
        {
            step: "2",
            title: "Enter Password",
            description: "Visit the link and enter the password provided separately for security"
        },
        {
            step: "3",
            title: "Download Securely",
            description: "Your file opens automatically with a 5-minute download window"
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">

            {/* Hero Section */}
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-indigo-600/10"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">

                    {/* Navigation */}
                    <nav className="flex justify-between items-center mb-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                                <Shield className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-xl font-bold text-gray-900">Leadloom</span>
                        </div>
                        {/* <div className="flex items-center gap-4">
              <button className="text-gray-600 hover:text-gray-900 font-medium transition-colors">
                Help
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                Contact
              </button>
            </div> */}
                    </nav>

                    {/* Hero Content */}
                    <div className="text-center max-w-4xl mx-auto">
                        <div className="mb-8">
                            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-8">
                                <CheckCircle className="w-4 h-4" />
                                Trusted by 10,000+ users worldwide
                            </div>
                        </div>

                        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                            Secure File Access
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600"> Made Simple</span>
                        </h1>

                        <p className="text-xl text-gray-600 mb-12 leading-relaxed max-w-2xl mx-auto">
                            Access password-protected files with enterprise-grade security. Each download link is unique, time-limited, and requires dual authentication.
                        </p>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                            <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-xl p-2 shadow-lg">
                                <input
                                    type="text"
                                    placeholder="Enter access key to try..."
                                    value={demoKey}
                                    onChange={(e) => setDemoKey(e.target.value)}
                                    className="px-4 py-3 bg-transparent border-none outline-none text-gray-700 placeholder-gray-500 min-w-64"
                                    onKeyDown={(e) => e.key === 'Enter' && handleDemoSubmit()}
                                />
                                <button
                                    onClick={handleDemoSubmit}
                                    disabled={!demoKey.trim()}
                                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 disabled:cursor-not-allowed"
                                >
                                    Access File
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Example URL */}
                        <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 max-w-2xl mx-auto">
                            <p className="text-sm text-gray-600 mb-3">Example access URL format:</p>
                            <div className="flex items-center justify-between bg-gray-100 rounded-lg p-3">
                                <code className="text-blue-600 font-mono">{url}</code>
                                <button
                                    onClick={() => copyToClipboard(url)}
                                    className="text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div className="py-24 bg-white/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Why Choose SecureFiles?
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Built with security-first principles to protect your sensitive files and data
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <div key={index} className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group">
                                <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <feature.icon className="w-7 h-7 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* How It Works Section */}
            <div className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            How It Works
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Simple 3-step process to access your secure files
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {steps.map((step, index) => (
                            <div key={index} className="relative">
                                {index < steps.length - 1 && (
                                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-blue-300 to-transparent transform translate-x-4"></div>
                                )}
                                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg text-center">
                                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-xl font-bold">
                                        {step.step}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            <div className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-3 gap-8 text-center">
                        <div className="text-white">
                            <div className="text-4xl md:text-5xl font-bold mb-2">99.9%</div>
                            <div className="text-blue-100 text-lg">Uptime Guarantee</div>
                        </div>
                        <div className="text-white">
                            <div className="text-4xl md:text-5xl font-bold mb-2">256-bit</div>
                            <div className="text-blue-100 text-lg">SSL Encryption</div>
                        </div>
                        <div className="text-white">
                            <div className="text-4xl md:text-5xl font-bold mb-2">5 min</div>
                            <div className="text-blue-100 text-lg">Auto Link Expiry</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="py-24 bg-white/50">
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                        Ready to Access Your File?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8">
                        If you have an access key, enter it above or paste your complete URL below
                    </p>

                    <div className="bg-gray-50 rounded-xl p-6 mb-8">
                        <div className="flex items-center gap-3 text-gray-700">
                            <Globe className="w-5 h-5" />
                            <span className="font-medium">Need help?</span>
                        </div>
                        <p className="text-gray-600 mt-2">
                            Contact the person who sent you the file if you don't have your access key or password.
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <div className="flex items-center gap-3 mb-4 md:mb-0">
                            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                                <Shield className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-lg font-bold">Leadloom</span>
                        </div>
                        {/* <div className="flex items-center gap-6 text-sm text-gray-400">
              <button className="hover:text-white transition-colors">Privacy Policy</button>
              <button className="hover:text-white transition-colors">Terms of Service</button>
              <button className="hover:text-white transition-colors">Support</button>
            </div> */}
                    </div>
                    <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
                        © 2025 Leadloom. All rights reserved. Built for secure file sharing.
                    </div>
                </div>
            </footer>
        </div>
    );
}